import useGetAdvancedFilterOptions from '../useGetAdvancedFilterOptions'
import { manaStringToElement } from 'components/misc/customIcons/ManaSymbol/helpers'

import Filter from 'components/deckPage/filtersAndControls/DeckFilter/ArchidektDeckFilter'
import { Option } from 'components/elements/ArchidektDropdown/types'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'

import styles from './archidektDeckFilter.module.scss'

type Filter = 'keywords' | 'types' | 'collection' | 'colors' | 'colorTags' | 'categories' | 'manaValues'

type selectAllProps = {
    filterKey: Filter, 
    filterValue: string | string[], 
    labelString?: string, 
    hidden?: boolean
}

type Props = {
  handleFilterSelection: (filterKey: Filter, filterValue: string | string[]) => void
  setFilters: (filter: Record<Filter, Record<string, boolean>>) => void
  filters: Record<Filter, Record<string, boolean>>
}

const DeckFilterDropdown = ({ handleFilterSelection, setFilters, filters }: Props) => {
  const [colors, keywords, types, superTypes, subTypes, colorTags, categories, collectionStatus] =
    useGetAdvancedFilterOptions()

  const manaValues: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  const colorList: string[] = ['White', 'Blue', 'Black', 'Red', 'Green', 'Colorless']

  const createSelectUnselectAll = ({filterKey, filterValue, labelString = '', hidden = false}: selectAllProps): Option[] => {
    return [
        {
            icon: 'check circle outline',
            label: `Toggle ${labelString} selection`,
            onClick: () => handleFilterSelection(filterKey, filterValue),
            noCloseOnClick: true,
            hidden: hidden,
        },
        {
            icon: 'circle outline',
            label: `Unselect all ${labelString}`,
            onClick: () => setFilters({ ...filters, [filterKey]: {} }),
            noCloseOnClick: true,
            hidden: hidden,
        },
        { type: 'spacer', hidden: hidden },
    ]
  }

  const keywordOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'keywords', filterValue: keywords, hidden: keywords.length<1}),
    ...keywords.map(
      (keyword): Option => ({
        type: 'checkbox',
        label: keyword,
        onChange: () => handleFilterSelection('keywords', keyword),
        checked: filters.keywords?.[keyword],
      }),
    ),
  ]

  const typeOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'types', filterValue: superTypes, labelString: 'super type', hidden: superTypes.length < 2}),
    ...superTypes.map(
      (superType): Option => ({
        type: 'checkbox',
        label: superType,
        onChange: () => handleFilterSelection('types', superType),
        checked: filters.types?.[superType],
      }),
    ),
    { type: 'spacer', hidden: superTypes.length === 0 },
    ...createSelectUnselectAll({filterKey: 'types', filterValue: types, labelString: 'type',  hidden: types.length < 2}),
    ...types.map(
      (type): Option => ({
        type: 'checkbox',
        label: type,
        onChange: () => handleFilterSelection('types', type),
        checked: filters.types?.[type],
      }),
    ),
    { type: 'spacer', hidden: types.length === 0 },
    ...createSelectUnselectAll({filterKey: 'types', filterValue: subTypes, labelString: 'sub type',  hidden: subTypes.length < 2}),
    ...subTypes.map(
      (subType): Option => ({
        type: 'checkbox',
        label: subType,
        onChange: () => handleFilterSelection('types', subType),
        checked: filters.types?.[subType],
      }),
    ),
  ]

  const collectionOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'collection', filterValue: collectionStatus, hidden: collectionStatus.length < 1}),
    ...collectionStatus.map(
      (status): Option => ({
        type: 'checkbox',
        label: status,
        onChange: () => handleFilterSelection('collection', status),
        checked: filters.collection?.[status],
      }),
    ),
  ]

  const colorOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'colors', filterValue: colors, hidden: colors.length < 1}),
    ...colorList.map(
      (color): Option => ({
        type: 'checkbox',
        label: color,
        onChange: () => handleFilterSelection('colors', color),
        checked: filters.colors?.[color],
      }),
    ),
  ]

  const colorTagOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'colorTags', filterValue: colorTags, hidden: colorTags.length < 2}),
    ...colorTags.map(
      (colorTag): Option => ({
        type: 'checkbox',
        label: colorTag === '' ? 'Default' : colorTag,
        onChange: () => handleFilterSelection('colorTags', colorTag),
        checked: filters.colorTags?.[colorTag],
      }),
    ),
  ]

  const categoryOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'categories', filterValue: categories, hidden: categories.length < 3}),
    ...categories.map(
      (category): Option => ({
        type: 'checkbox',
        label: category,
        onChange: () => handleFilterSelection('categories', category),
        checked: filters.categories?.[category],
      }),
    ),
  ]

  const manaValueOptions: Option[] = [
    ...createSelectUnselectAll({filterKey: 'manaValues', filterValue: manaValues}),
    ...manaValues.map(
      (manaValue): Option => ({
        className: styles.flex,
        type: 'checkbox',
        label: manaStringToElement(`{${manaValue}}`),
        onChange: () => handleFilterSelection('manaValues', manaValue),
        checked: filters.manaValues?.[manaValue],
      }),
    ),
  ]

  return (
    <ArchidektDropdown
      menuClassName={styles.addFilterMenu}
      options={[
        {
          type: 'extras',
          label: 'Keywords',
          id: 'keywords',
          className: styles.addFilterMenuSubmenu,
          options: keywordOptions,
        },
        {
          type: 'extras',
          label: 'Types',
          id: 'types',
          className: styles.addFilterMenuSubmenu,
          options: typeOptions,
        },
        {
          type: 'extras',
          label: 'Collection',
          id: 'collection',
          options: collectionOptions,
        },
        {
          type: 'extras',
          label: 'Colors',
          id: 'colors',
          options: colorOptions,
        },
        {
          type: 'extras',
          label: 'Color tags',
          id: 'color tags',
          className: styles.addFilterMenuSubmenu,
          options: colorTagOptions,
        },
        {
          type: 'extras',
          label: 'Categories',
          id: 'categories',
          className: styles.addFilterMenuSubmenu,
          options: categoryOptions,
        },
        {
          type: 'extras',
          label: 'Mana values',
          id: 'mana values',
          className: styles.addFilterMenuSubmenu,
          options: manaValueOptions,
        },
      ]}>
      <PhatButton className={styles.filterDropdown}>
        <Icon name="plus" />
      </PhatButton>
    </ArchidektDropdown>
  )
}

export default DeckFilterDropdown
